import { useState } from "react";
import JourneyHeader from "./JourneyHeader";
import { Ancillary, Journey, Travel } from "@gen/travel/v1/common_pb";
import ProductsDisplay from "./Product";
import { productsByPassenger, productsOfJourney } from "@services/travel";
import { useTranslation } from "react-i18next";
import { Icon } from "@thetreep/ui";
import theme from "@theme";
import { formatPrice } from "@utils/formatPrice";
import DetailedTimeline from "./DetailedTimeline";

type JourneyDisplayProps = {
  travel: Travel;
  journey: Journey;
  showStatus?: boolean;
};

export default function JourneyDisplay({
  travel,
  journey,
  showStatus,
}: JourneyDisplayProps) {
  const [collapsed, setCollapsed] = useState(true);

  if (!journey.schedule?.departure || !journey.schedule?.arrival) {
    return;
  }

  function getAncillaries(): Ancillary[] {
    const products = productsOfJourney(travel, journey);
    const ancillaryMap = new Map<string, Ancillary>();

    for (const product of products) {
      for (const a of product.ancillaries) {
        const existing = ancillaryMap.get(a.type);
        if (existing) {
          existing.quantity += a.quantity;
        } else {
          ancillaryMap.set(a.type, { ...a });
        }
      }
    }

    return Array.from(ancillaryMap.values());
  }

  return (
    <div
      className="border border-grey rounded-md p-4 bg-white w-full h-full
                flex flex-col justify-between gap-4"
    >
      <JourneyHeader
        travel={travel}
        journey={journey}
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        showStatus={showStatus}
      />
      {!collapsed && (
        <div className="flex flex-col gap-12 md:flex-row">
          <div className="">
            <DetailedTimeline journey={journey} products={travel.products} />
          </div>
          <div className="flex flex-col gap-4">
            {productsByPassenger(travel, journey).map(
              (productsByPassenger, index) => (
                <ProductsDisplay
                  key={index}
                  journey={journey}
                  travel={travel}
                  products={productsByPassenger.products}
                  passenger={productsByPassenger.passenger}
                />
              ),
            )}
            {getAncillaries().length > 0 && (
              <Ancillaries ancillaries={getAncillaries()} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Ancillaries({ ancillaries }: { ancillaries: Ancillary[] }) {
  const { t } = useTranslation();
  function price() {
    let amount = 0;
    ancillaries.forEach(
      (a) => (amount += Number(a.price?.amount) * a.quantity),
    );
    return formatPrice({
      amount: BigInt(amount),
      currency: ancillaries[0]?.price?.currency,
    });
  }

  return (
    ancillaries.length > 0 && (
      <div className="flex flex-col gap-2 ml-6">
        <div className="flex gap-2 justify-between w-full">
          <span className="">{t("options.title")}</span>
          <div className="flex items-center font-bold text-base text-text-strong">
            {price()}
          </div>
        </div>
        {ancillaries.map((a) => (
          <AncillaryTag key={a.type} ancillary={a} />
        ))}
      </div>
    )
  );
}

function AncillaryTag({ ancillary }: { ancillary: Ancillary }) {
  const { t } = useTranslation();

  let info: { icon: string; key: string };
  switch (ancillary.type) {
    case "ouigo-flex":
      info = {
        icon: "shuffle_2_line",
        key: "options.ouigoFlex",
      };
      break;
    case "additional-luggage":
      info = {
        icon: "luggage_line",
        key: "options.luggage",
      };
      break;
    case "bike":
      info = {
        icon: "bike_line",
        key: "options.nonFoldedBike",
      };
      break;
    case "disassembled-bike":
      info = {
        icon: "bike_line",
        key: "options.foldedBike",
      };
      break;
    default:
      info = {
        icon: "add_circle_line",
        key: ancillary.type,
      };
  }

  return (
    <div className="flex flex-row items-center justify-between font-cnam gap-1 text-sm w-full">
      <div className="flex gap-1">
        <Icon
          color={theme.colors.cnam.blue.DEFAULT}
          size={24}
          src={info.icon}
        />
        {ancillary.quantity > 1 && <span>{ancillary.quantity}x</span>}
        <span>{t(info.key)}</span>
      </div>
      <div className="flex gap-1">
        {ancillary.quantity > 1 && <span>{ancillary.quantity}x</span>}
        {formatPrice(ancillary.price)}
      </div>
    </div>
  );
}
